import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {Tabs} from "components/Tabs";
import {inject, observer} from "mobx-react";
// local
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {IconLock, IconHourglass, IconPlusCircle, IconForward} from "components/icons";
import {ProjectModal} from "components/modals/ProjectModal";
import {ProcurementPlaceProjectModal} from "components/modals/ProcurementPlaceProjectModal";
import {toRequestId, CONTRACTS_TO_US} from "constants/routes.const";
import {procurementStatuses} from "constants/buyer.consts";
import {LockValue} from "components/LockValue";
import {hasPermission, hasPermissions} from "helpers/helper";
import {color} from "constants/color.consts";

const haveDocuments = (request) =>
  request.documents
    .map((doc) => [...doc.contracts, ...doc.documents, ...doc.plans, ...doc.links])
    .flat().length > 0;

const SingleRequestContent = inject("store")(
  observer(({store: {auth, buyer, seller}}) => {
    const {
      request,
      requestToUsTabs,
      activeRequestTab,
      currentContract,
      isFinishedContract,
      isContract,
    } = seller;
    const {user} = auth;

    const location = useLocation();
    const navigate = useNavigate();

    const [projects, setProjects] = useState([]);
    const [selectedProjectForCopyProcurement, setSelectedProjectForCopyProcurement] =
      useState();
    const [isProcurementPlaceProjectModal, setProcurementPlaceProjectModal] =
      useState(false);
    const [copyProcurementLoading, setCopyProcurementLoading] = useState(false);
    const [isProjectModal, setIsProjectModal] = useState(null);

    const fromMessageNotifications =
      (request.id && location?.state?.messageProposalId === "seller") ||
      location?.search?.includes("?messageProposalId=seller");
    const unreadMessagesCount = request.MessagesStat?.unreadMessageCount;
    const totalMessagesCount = request.MessagesStat?.totalMessageCount;
    const isSubmitted = !!request.Offer?.id;
    const isStoppedProcurement =
      request?.Request?.ProjectItem?.status === procurementStatuses.STOPPED;
    const copyAsProcurementPermissions = [
      "CREATE_PROCUREMENT_FROM_REQUEST",
      "CREATE_PROJECT",
      "READ_PROJECTS",
      "CREATE_PROCUREMENT",
      "SHOW_UNPUBLISHED_RFT",
      "EDIT_PROCUREMENT",
      "CREATE_RFT",
      "EDIT_RFT",
    ];

    const onSubmitted = () =>
      hasPermission(["READ_SELLER_RFTS"]) && seller.setActiveRequestTab(1);

    const handleCopyAsProcurement = () => {
      if (!hasPermissions(copyAsProcurementPermissions)) return;
      setCopyProcurementLoading(true);
      buyer
        .getProjects(true, {tab: "all", page: 1, size: 9999})
        .then((data) => setProjects(data?.rows))
        .then(() => setProcurementPlaceProjectModal(true))
        .finally(() => setCopyProcurementLoading(false));
    };

    const handleCreateProject = () => setIsProjectModal("new-project-with-procurement");

    const handleCreateProcurementWithRfp = async (project) => {
      try {
        setCopyProcurementLoading(true);
        !projects.find((item) => item.id === project.id) &&
          setProjects((prevState) => [...prevState, project]);
        setSelectedProjectForCopyProcurement(project);
        const procurementPayload = {
          name: request.Request?.name,
          UserId: user.id,
        };
        const procurement = await buyer.createProcurement(project.id, procurementPayload);
        const rfpPayload = {
          name: procurement.name,
          ProjectId: project.id,
          projectItemId: procurement.id,
          location: request.Request?.location,
          locationLat: request.Request?.locationLat,
          locationLng: request.Request?.locationLng,
          description: request.Request?.description,
          ContactPersonId: user.id,
          CurrencyCode: request.Request?.CurrencyCode,
          smartFormQuestions: request.Request?.smartFormQuestions.map((item) => ({
            ...item,
            id: window.crypto.randomUUID(),
          })),
        };
        let documents = [];
        if (haveDocuments(request.Request))
          documents = await buyer.duplicateRFPDocuments(request.Request?.id);

        buyer.getCurrentRequestInfo("draft-request");
        const data = await buyer.updateCurrentRequestInfo(
          {...rfpPayload, documents},
          true
        );

        navigate(toRequestId(data.rfp.id));
      } catch (error) {
      } finally {
        setCopyProcurementLoading(false);
      }
    };

    const tabLine = <div className="request-to-us__tabs__tab-line">|</div>;

    const CreateOfferButton = isSubmitted ? undefined : (
      <React.Fragment>
        <LockValue
          canPermissions={["READ_SELLER_RFTS"]}
          needLabel
          needShowModalToSubscribe
          addClassIcon="mb-1 mr-2 ml-0"
        >
          <ButtonTransparent
            nonTransparent
            small
            addClass="create-offer-button"
            name="CREATE_OFFER"
            icon={<IconPlusCircle />}
            disabled={isStoppedProcurement}
            onClick={onSubmitted}
          />
        </LockValue>
        <LockValue
          canPermissions={copyAsProcurementPermissions}
          needAllPermissions
          needLabel
          needShowModalToSubscribe
          addClassIcon="mb-1 mr-2 ml-0"
        >
          <ButtonTransparent
            small
            addClass="create-offer-button"
            name="COPY_AS_PROCUREMENT"
            icon={<IconForward />}
            loading={copyProcurementLoading}
            onClick={handleCopyAsProcurement}
            tooltip="COPY_AS_PROCUREMENT_TOOLTIP"
          />
        </LockValue>
      </React.Fragment>
    );

    useEffect(() => {
      const contractId = location?.state?.contractId || request.Offer?.Contract?.id;
      location.state?.offerId && seller.setOfferId(location.state?.offerId);
      seller.setContractStatus(!!contractId);
      const isGettingContract = !!contractId && contractId !== currentContract?.id;

      // set Messages tab to active if you clicked on one of the top notifications
      seller.setActiveRequestTab(
        isGettingContract && activeRequestTab !== 2
          ? 2
          : location.state?.from === "offers"
          ? 1
          : fromMessageNotifications
          ? 3
          : activeRequestTab
      );
      contractId &&
        contractId !== currentContract?.id &&
        seller.getContract(contractId).catch(() => navigate(CONTRACTS_TO_US));
    }, [
      request,
      fromMessageNotifications,
      activeRequestTab,
      currentContract,
      seller,
      location,
      navigate,
    ]);

    return (
      <React.Fragment>
        <Tabs
          tabs={requestToUsTabs}
          active={activeRequestTab}
          setActive={(id) => seller.setActiveRequestTab(id)}
          counts={[
            null,
            null,
            isFinishedContract ? (
              <IconLock width={16} height={16} />
            ) : isContract ? (
              <IconHourglass width={16} height={16} color={color.black} />
            ) : null,
            unreadMessagesCount ? unreadMessagesCount : totalMessagesCount ?? null,
          ]}
          redCounts={[null, null, null, unreadMessagesCount]}
          addClass="presentation-page__right-side__tabs request-to-us__tabs"
          leftContent={{OUR_OFFER: CreateOfferButton, CHAT: tabLine}}
        />
        {isProcurementPlaceProjectModal && (
          <ProcurementPlaceProjectModal
            procurement={request}
            projects={projects}
            selectedProject={selectedProjectForCopyProcurement ?? projects[0]}
            onSelectProject={setSelectedProjectForCopyProcurement}
            loading={copyProcurementLoading}
            onCreateInNewProject={handleCreateProject}
            onCreateInExitingProject={handleCreateProcurementWithRfp}
            loggedUser={user}
            onClose={() => setProcurementPlaceProjectModal(false)}
          />
        )}
        {isProjectModal && (
          <ProjectModal
            projectId={isProjectModal}
            initialValues={{
              location: request.Request?.ProjectItem?.Project?.location,
              availableIn: request.Request?.ProjectItem?.Project?.availableIn ?? [],
              buyer: request.Request?.Buyer?.name,
            }}
            onCreateProcurement={handleCreateProcurementWithRfp}
            onClose={() => setIsProjectModal(null)}
          />
        )}
      </React.Fragment>
    );
  })
);

export default SingleRequestContent;
